import React, {
  useCallback, useEffect, useState
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { PersonMultiSelect } from '../../components/v2/person-multi-select';
import { Person } from '../../types';
import { VaultMessage } from '../../types/vault';

import { vaultClient } from './vault-client';

export function VaultEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [ message, setMessage ] = useState<VaultMessage | null>(null);
  const [ title, setTitle ] = useState('');
  const [ content, setContent ] = useState('');
  const [ password, setPassword ] = useState('');
  const [ confirmPassword, setConfirmPassword ] = useState('');
  const [ selectedRecipients, setSelectedRecipients ] = useState<Person[]>([]);
  const [ showPersonSelect, setShowPersonSelect ] = useState(false);
  const [ loading, setLoading ] = useState(true);
  const [ saving, setSaving ] = useState(false);
  const [ error, setError ] = useState<string | null>(null);

  const loadMessage = useCallback(async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const data = await vaultClient.find(id);
      if (data) {
        setMessage(data);
        setTitle(data.title);
        setContent(data.content);
        setSelectedRecipients(data.recipients);
        // Don't pre-fill password for security
      } else {
        setError('Message not found');
      }
    } catch (err) {
      setError('Failed to load message');
    } finally {
      setLoading(false);
    }
  }, [ id ]);

  useEffect(() => {
    if (id) {
      loadMessage();
    }
  }, [ id, loadMessage ]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    
    setError(null);

    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    if (!content.trim()) {
      setError('Message content is required');
      return;
    }

    if (password && password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (selectedRecipients.length === 0) {
      setError('Please select at least one recipient');
      return;
    }

    try {
      setSaving(true);
      await vaultClient.update(id, {
        id,
        title: title.trim(),
        content: content.trim(),
        password: password || undefined,
        recipientIds: selectedRecipients.map(p => p.id)
      });
      navigate(`/vault/${id}`);
    } catch (err) {
      setError('Failed to update message');
    } finally {
      setSaving(false);
    }
  };

  const handleRecipientsSelect = (recipients: Person[]) => {
    setSelectedRecipients(recipients);
    setShowPersonSelect(false);
  };

  const handleRemoveRecipient = (recipientId: string) => {
    setSelectedRecipients(prev => prev.filter(p => p.id !== recipientId));
  };

  const handleCancel = () => {
    if (id) {
      navigate(`/vault/${id}`);
    } else {
      navigate('/vault');
    }
  };

  if (loading) {
    return (
      <div className="vault-page">
        <div className="screen-container">
          <div className="screen-header">
            <div className="screen-header-back-button">
              <button className="back-button" onClick={handleCancel}>
                <span className="material-symbols-outlined">arrow_back</span>
              </button>
            </div>
            <div className="screen-title">
              <h2>Loading Message...</h2>
            </div>
          </div>
          <div className="screen-content">
            <div className="loading-container">
              <p>Loading message...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !message) {
    return (
      <div className="vault-page">
        <div className="screen-container">
          <div className="screen-header">
            <div className="screen-header-back-button">
              <button className="back-button" onClick={handleCancel}>
                <span className="material-symbols-outlined">arrow_back</span>
              </button>
            </div>
            <div className="screen-title">
              <h2>Error</h2>
            </div>
          </div>
          <div className="screen-content">
            <div className="error-container">
              <p className="error-message">{error || 'Message not found'}</p>
              <button className="button" onClick={() => navigate('/vault')}>
                Back to Vault
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="vault-page">
      <div className="screen-container">
        <div className="screen-header">
          <div className="screen-header-back-button">
            <button className="back-button" onClick={handleCancel}>
              <span className="material-symbols-outlined">arrow_back</span>
            </button>
          </div>
          <div className="screen-title">
            <h2>Edit Message</h2>
            <p>Update your secret message</p>
          </div>
        </div>

        <div className="screen-content">
          <form onSubmit={handleSubmit} className="vault-form">
            {error && (
              <div className="error-container">
                <p className="error-message">{error}</p>
              </div>
            )}

            <div className="input">
              <label htmlFor="title">Message Title</label>
              <input
                id="title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter a title for your message"
                required
              />
            </div>

            <div className="input">
              <label htmlFor="content">Message Content</label>
              <textarea
                id="content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Write your message here..."
                rows={8}
                required
              />
            </div>

            <div className="input">
              <label htmlFor="password">New Password (Optional)</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter a new password to lock this message"
              />
              <p className="input-help">
                Leave blank to keep current password. If you set a new password, only you will be able to read this message.
              </p>
            </div>

            {password && (
              <div className="input">
                <label htmlFor="confirmPassword">Confirm New Password</label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your new password"
                />
              </div>
            )}

            <div className="input">
              <label>Recipients</label>
              <div className="recipients-container">
                {selectedRecipients.length > 0 && (
                  <div className="selected-recipients">
                    {selectedRecipients.map((recipient) => (
                      <div key={recipient.id} className="selected-recipient">
                        <span className="recipient-name">
                          {recipient.firstName} {recipient.lastName}
                        </span>
                        <button
                          type="button"
                          className="remove-recipient"
                          onClick={() => handleRemoveRecipient(recipient.id)}
                        >
                          <span className="material-symbols-outlined">close</span>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                <button
                  type="button"
                  className="button secondary add-recipients-button"
                  onClick={() => setShowPersonSelect(true)}
                >
                  <span className="material-symbols-outlined">person_add</span>
                  {selectedRecipients.length === 0 ? 'Add Recipients' : 'Add More Recipients'}
                </button>
              </div>
            </div>

            <div className="button-container">
              <button
                type="button"
                className="button secondary"
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="button"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {showPersonSelect && (
        <div className="modal-overlay">
          <div className="modal-content">
            <PersonMultiSelect
              selected={selectedRecipients}
              handleSelectOptions={handleRecipientsSelect}
              handleClickableOption={() => {
                // Handle adding new person - could navigate to person creation
                console.log('Add new person clicked');
              }}
              multiple={true}
              handleCancel={() => setShowPersonSelect(false)}
              inModal={true}
            />
          </div>
        </div>
      )}
    </div>
  );
}
